/**
 * SF-4762 — exempt email list for a flag with `exemptAccounts`.
 *
 * Add and remove change a local draft; "Save exempt list" (or "Save list" for
 * a flag with its own listLabel, SF-4801) sends the whole
 * list in one PUT, so one save is one history row. A bad or duplicate email,
 * or going over sf-api's cap, is refused here with a clear message before
 * any request (sf-api checks the same and stays the authority).
 *
 * The draft only takes the stored list when it really changed and there are
 * no unsaved edits, or after this list's own save: an address typed but not
 * saved survives another save on the card (e.g. the on/off switch).
 */
import { useState } from 'react';
import { Button, Input, Space, Tag, Typography } from 'antd';
import { isValidEmail, MAX_EXEMPT_EMAILS, normaliseEmail } from './api/platformFlagsClient';
import { EXEMPT_LIST_WORDING, type ListWording } from './format';

const { Text } = Typography;

export interface ExemptEmailsEditorProps {
  saved: string[];
  canWrite: boolean;
  /** This list's own save is running (the button spins). */
  saving: boolean;
  /** Any save on the card is running (the controls are locked). */
  busy: boolean;
  onSave: (next: string[]) => Promise<boolean>;
  /** SF-4801 — the flag's list wording (default: the exempt-list wording). */
  wording?: ListWording;
}

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((email, i) => email === b[i]);

export const ExemptEmailsEditor: React.FC<ExemptEmailsEditorProps> = ({
  saved,
  canWrite,
  saving,
  busy,
  onSave,
  wording = EXEMPT_LIST_WORDING,
}) => {
  const [draft, setDraft] = useState<string[]>(saved);
  // The stored list the draft was last compared with.
  const [base, setBase] = useState<string[]>(saved);
  const [ownSave, setOwnSave] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!sameList(saved, base)) {
    setBase(saved);
    if (ownSave || sameList(draft, base)) setDraft(saved);
  }

  const save = async () => {
    setOwnSave(true);
    try {
      await onSave(draft);
    } finally {
      setOwnSave(false);
    }
  };

  const add = () => {
    const email = normaliseEmail(input);
    if (!isValidEmail(email)) {
      setError(`"${input.trim()}" is not a valid email address.`);
      return;
    }
    if (draft.includes(email)) {
      setError(`${email} is already on the list.`);
      return;
    }
    if (draft.length >= MAX_EXEMPT_EMAILS) {
      setError(`The list can hold at most ${MAX_EXEMPT_EMAILS} addresses.`);
      return;
    }
    setDraft([...draft, email]);
    setInput('');
    setError(null);
  };

  const locked = !canWrite || busy;
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }} data-testid="exempt-emails">
      <Text strong>{wording.heading}</Text>
      {wording.help && <Text type="secondary">{wording.help}</Text>}
      <div>
        {draft.length === 0 && <Text type="secondary">{wording.empty}</Text>}
        {draft.map((email) => (
          <Tag
            key={email}
            closable={!locked}
            closeIcon={!locked ? <span aria-label={`Remove ${email}`}>×</span> : undefined}
            onClose={(e) => {
              e.preventDefault();
              setDraft(draft.filter((d) => d !== email));
            }}
          >
            {email}
          </Tag>
        ))}
      </div>
      {canWrite && (
        <>
          <Space.Compact style={{ maxWidth: 460, width: '100%' }}>
            <Input
              aria-label={wording.inputLabel}
              placeholder="name@servefirst.co.uk"
              value={input}
              disabled={busy}
              status={error ? 'error' : undefined}
              onChange={(e) => {
                setInput(e.target.value);
                setError(null);
              }}
              onPressEnter={add}
            />
            <Button onClick={add} disabled={busy || input.trim() === ''}>
              Add
            </Button>
          </Space.Compact>
          {error && <Text type="danger">{error}</Text>}
          <Button
            type="primary"
            loading={saving}
            disabled={sameList(draft, saved) || (busy && !saving)}
            onClick={() => void save()}
          >
            {wording.saveText}
          </Button>
        </>
      )}
    </Space>
  );
};
